import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Edit3, EyeOff, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useVenue } from '../../contexts/VenueContext';
import { getAuthHeaders, getVenueParams } from '../../utils/venueAppApi';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";
const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
function getDaysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function getFirstDayOfWeek(y, m) { return (new Date(y, m, 1).getDay() + 6) % 7; }

export default function VenueCalendarPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const { activeVenueAppId } = useVenue();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchBlockedDates(); }, [activeVenueAppId]);
  const fetchBlockedDates = async () => {
    try {
      const res = await axios.get(`${API}/venue/blocked-dates`, { headers: getAuthHeaders(), params: getVenueParams(activeVenueAppId) });
      setBlockedDates(res.data);
    } catch (err) { console.error(err); }
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
  const makeDateStr = (d) => `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const handleDayClick = (d) => {
    const ds = makeDateStr(d);
    if (!rangeStart || (rangeStart && rangeEnd)) { setRangeStart(ds); setRangeEnd(null); }
    else { if (ds >= rangeStart) setRangeEnd(ds); else { setRangeEnd(rangeStart); setRangeStart(ds); } }
  };
  const isInSelection = (d) => { if (!rangeStart) return false; const ds = makeDateStr(d); if (!rangeEnd) return ds === rangeStart; return ds >= rangeStart && ds <= rangeEnd; };
  const isBlocked = (d) => { const ds = makeDateStr(d); return blockedDates.some(b => b.start_date <= ds && b.end_date >= ds); };

  const handleBlockDates = async () => {
    if (!rangeStart) return;
    setSaving(true);
    try {
      await axios.post(`${API}/venue/blocked-dates`, { start_date: rangeStart, end_date: rangeEnd || rangeStart, reason: 'Closed', venue_app_id: activeVenueAppId }, { headers: getAuthHeaders() });
      toast.success('Dates blocked'); setRangeStart(null); setRangeEnd(null); fetchBlockedDates();
    } catch (err) { toast.error('Failed'); } finally { setSaving(false); }
  };
  const handleOpenDates = async () => {
    if (!rangeStart) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/venue/blocked-dates`, { data: { start_date: rangeStart, end_date: rangeEnd || rangeStart, venue_app_id: activeVenueAppId }, headers: getAuthHeaders() });
      toast.success('Dates opened'); setRangeStart(null); setRangeEnd(null); fetchBlockedDates();
    } catch (err) { toast.error('Failed'); } finally { setSaving(false); }
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const hasSelection = !!rangeStart;
  const selectionHasBlocked = hasSelection && (() => { const end = rangeEnd || rangeStart; return blockedDates.some(b => b.start_date <= end && b.end_date >= rangeStart); })();
  // Selection covers only future-or-today dates that are NOT already blocked —
  // this is the case where tapping "Block" will actually hide the venue from
  // the guest map. Trigger the friendly preview note in that case.
  const selectionWillHide = hasSelection && !selectionHasBlocked && (rangeStart >= todayStr);
  // Count upcoming blocked dates so we can show a persistent banner letting
  // the owner know their venue is invisible on those days.
  const upcomingBlockedCount = blockedDates.reduce((acc, b) => {
    const start = b.start_date > todayStr ? b.start_date : todayStr;
    if (start > b.end_date) return acc;
    const startDt = new Date(start);
    const endDt = new Date(b.end_date);
    const days = Math.max(0, Math.round((endDt - startDt) / (1000 * 60 * 60 * 24)) + 1);
    return acc + days;
  }, 0);

  const formatHumanDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  };
  const selectionLabel = hasSelection
    ? (rangeEnd && rangeEnd !== rangeStart
        ? `${formatHumanDate(rangeStart)} – ${formatHumanDate(rangeEnd)}`
        : formatHumanDate(rangeStart))
    : '';

  return (
    <div className="app-shell" style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40 }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-start', padding: '16px 20px 0' }}>
        <button onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><ChevronLeft size={30} color="var(--text)" strokeWidth={2} /></button>
      </div>
      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-calendar-shared-logo" />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <h2 style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: 'var(--text)' }}>Calendar</h2>
        <Edit3 size={22} color="var(--text)" />
      </div>

      {upcomingBlockedCount > 0 ? (
        <div
          data-testid="venue-calendar-hidden-banner"
          style={{
            width: 'calc(100% - 32px)',
            maxWidth: 360,
            marginTop: 14,
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            borderRadius: 18,
            background: 'var(--status-danger-surface)',
            border: '1px solid var(--status-danger-text)',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: 'var(--status-danger-surface)',
              color: 'var(--status-danger-text)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <EyeOff size={18} strokeWidth={2} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--status-danger-text)', lineHeight: 1.2 }}>
              Hidden from the guest map
            </p>
            <p style={{ margin: '4px 0 0', fontFamily: font, fontSize: 13, color: 'var(--status-danger-text)', lineHeight: 1.3 }}>
              Your venue won&apos;t appear on the guest map for the {upcomingBlockedCount} upcoming blocked {upcomingBlockedCount === 1 ? 'day' : 'days'}.
            </p>
          </div>
        </div>
      ) : null}

      <div style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 16, backgroundColor: 'var(--app-bg)', borderRadius: 20, padding: '16px 12px', border: '1px solid var(--card-border)', boxShadow: 'var(--premium-shadow-main)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <button onClick={() => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: font, fontSize: 22, color: 'var(--text)' }}>&#8249;</button>
          <span style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{MONTHS_FULL[viewMonth]} {viewYear}</span>
          <button onClick={() => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: font, fontSize: 22, color: 'var(--text)' }}>&#8250;</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 6 }}>
          {DAYS.map(d => (<div key={d} style={{ textAlign: 'center', fontFamily: font, fontSize: 14, fontWeight: 700, color: 'var(--text-3)', padding: '4px 0' }}>{d}</div>))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {cells.map((day, i) => {
            if (day === null) return <div key={`empty-${viewYear}-${viewMonth}-${i}`} />;
            const inSel = isInSelection(day); const blocked = isBlocked(day);
            const start = makeDateStr(day) === rangeStart; const end = makeDateStr(day) === rangeEnd;
            const dayKey = `venue-cal-${makeDateStr(day)}`;
            let bg = 'transparent', color = 'var(--text)', fw = 400, br = '50%';
            if (inSel) { bg = start || end ? '#E53935' : '#F28B82'; color = '#fff'; fw = 700; br = start ? '50% 0 0 50%' : end ? '0 50% 50% 0' : '0'; }
            else if (blocked) { bg = 'var(--status-danger-surface)'; color = 'var(--status-danger-text)'; fw = 600; }
            return (
              <button key={dayKey} data-testid={dayKey} onClick={() => handleDayClick(day)} style={{ width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: font, fontSize: 16, fontWeight: fw, color, backgroundColor: bg, borderRadius: br, border: makeDateStr(day) === todayStr && !inSel ? '2px solid #E53935' : 'none', cursor: 'pointer', padding: 0, position: 'relative' }}>
                {day}
                {blocked && !inSel && <div style={{ position: 'absolute', bottom: 2, width: 4, height: 4, borderRadius: '50%', backgroundColor: 'var(--status-danger-text)' }} />}
              </button>
            );
          })}
        </div>
        {rangeStart && (
          <div style={{ marginTop: 12, textAlign: 'center', fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>
            <span style={{ fontWeight: 700 }}>From:</span> {rangeStart}
            {rangeEnd && <><span style={{ margin: '0 8px' }}>→</span><span style={{ fontWeight: 700 }}>Until:</span> {rangeEnd}</>}
          </div>
        )}
      </div>

      {selectionWillHide ? (
        <div
          data-testid="venue-calendar-will-hide-note"
          style={{
            width: 'calc(100% - 32px)',
            maxWidth: 360,
            marginTop: 12,
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            borderRadius: 14,
            background: 'rgba(245, 166, 35, 0.08)',
            border: '1px solid rgba(245, 166, 35, 0.28)',
          }}
        >
          <EyeOff size={16} color="#B36A00" strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ margin: 0, fontFamily: font, fontSize: 13, color: 'var(--status-warning-text)', lineHeight: 1.35 }}>
            If you block <strong>{selectionLabel}</strong>, your venue won&apos;t be visible on the guest map for {rangeEnd && rangeEnd !== rangeStart ? 'those days' : 'that day'}.
          </p>
        </div>
      ) : null}

      {hasSelection && (
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <button onClick={handleBlockDates} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', fontFamily: font, fontSize: 18, fontWeight: 700, color: '#fff', backgroundColor: '#E53935', border: 'none', borderRadius: 12, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            <Lock size={18} /> Block
          </button>
          {selectionHasBlocked && (
            <button onClick={handleOpenDates} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', fontFamily: font, fontSize: 18, fontWeight: 700, color: '#fff', backgroundColor: '#1F8E2A', border: 'none', borderRadius: 12, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              <Unlock size={18} /> Open
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: 'var(--status-danger-surface)', border: '1px solid var(--status-danger-text)' }} /><span style={{ fontFamily: font, fontSize: 14, color: 'var(--text-3)' }}>Blocked</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--brand-red)' }} /><span style={{ fontFamily: font, fontSize: 14, color: 'var(--text-3)' }}>Today</span></div>
      </div>
    </div>
  );
}
